import {
  BarChart3,
  Calendar,
  Globe,
  Instagram,
  Monitor,
  Settings,
  Sparkles,
} from "lucide-react";
import type { SiteContent } from "./content.types";

export const contentEs: SiteContent = {
  home: {
    hero: {
      title: "Conseguí más reservas con un sistema que trabaja 24/7",
      subtitle:
        "Instagram, web, reservas online y automatización trabajando juntos para que tu agenda se llene sin vivir pendiente del móvil.",
      diagramLabel:
        "Diagrama del Sistema Lucentia: Instagram, página web, reservas, automatización y optimización convergen en tu negocio y terminan en una reserva confirmada.",
      center: "Tu negocio",
      centerDone: "Agenda en marcha",
      channels: ["Instagram", "Web", "Reservas", "Automatización", "Optimización"],
      booking: { title: "Reserva confirmada", detail: "Sábado · 11:00" },
      byline: { name: "Jenifer Roelstraete", role: "Estrategia digital para negocios con reservas" },
    },
    nichesLabel: "Negocios con los que trabajamos",
    niches: [
      "Peluquerías",
      "Centros de estética",
      "Centros de Manicura",
      "Estudios de tatuajes y piercing",
      "Fisioterapeutas",
      "Nutricionistas",
      "Clínicas",
      "Fotógrafos",
      "Peluquerías caninas",
    ],
    problem: {
      title: "Publicás, respondés mensajes todo el día y aun así la agenda no se llena.",
      lines: [
        "Publicás en Instagram, pero sin una estrategia detrás.",
        "Tenés una web que se ve bien, pero no genera reservas.",
        "Y pasás el día contestando WhatsApp en lugar de atender a tus clientes.",
      ],
      conclusion: "No es falta de esfuerzo. Es falta de sistema.",
    },
    system: {
      title: "Cinco piezas, un solo sistema",
      subtitle:
        "El Sistema Lucentia® conecta cada parte de tu presencia digital para que trabajen juntas: atraer, convertir, reservar, automatizar y mejorar.",
      stepLabel: "Pieza",
      cta: "Conocé el Sistema Lucentia®",
    },
    services: {
      title: "Elegí por dónde empezar",
      subtitle: "No todos los negocios necesitan lo mismo. Cada servicio se puede contratar por separado o combinar dentro del sistema.",
      fromLabel: "desde",
      addOnsTitle: "Y si lo necesitás",
    },
    plans: {
      title: "Planes claros, sin letra pequeña",
      subtitle: "Cada plan suma más contenido y más acompañamiento. Los rayos muestran las publicaciones de cada mes.",
      postsLabel: "publicaciones al mes",
      foundationLabel: "la base de todo",
      cta: "Ver precios, servicios sueltos y condiciones",
    },
    process: {
      title: "Empezar es simple",
      subtitle: "Tres pasos, siempre sabiendo qué hacemos y por qué.",
      steps: [
        { title: "Auditoría gratuita", desc: "Me escribís por WhatsApp. Analizo tu perfil y tu negocio y te digo qué está frenando tus reservas. Sin compromiso." },
        { title: "Tu plan a medida", desc: "Te presento el diagnóstico y el plan que tiene sentido para vos: pilares, calendario y contenidos con una intención clara." },
        { title: "Ejecución y mejora", desc: "Publicamos, cuidamos tu comunidad y medimos cada mes para ajustar y seguir mejorando." },
      ],
    },
    roi: {
      title: "¿Cuántas reservas necesitás para que el plan se pague solo?",
      lead: "Hacé la cuenta con lo que cobrás de verdad.",
      ticketLabel: "Lo que cobrás de media por cita (€)",
      planLabel: "Plan",
      resultOne: "Con {count} reserva nueva al mes, el plan {plan} se paga solo.",
      resultMany: "Con {count} reservas nuevas al mes, el plan {plan} se paga solo.",
      note: "Es una cuenta orientativa, no una promesa de resultados: depende de tu negocio, tu oferta y tu constancia.",
    },
    about: {
      quote: "No creo en publicar por publicar. Creo en construir marcas que conecten y crezcan de forma sostenible.",
      signature: "Jenifer Roelstraete, fundadora de Lucentia Social",
    },
    resources: {
      title: "Recursos gratuitos para tu negocio",
      lead: "Guías descargables para optimizar tu perfil y entender cómo Instagram, web y reservas trabajan juntos.",
      cta: "Ver todos los recursos",
    },
    contact: {
      title: "Contacto",
      lead: "Contame qué necesita tu negocio. Te respondo en menos de 24 horas, de lunes a viernes.",
      name: "Nombre",
      email: "Correo electrónico",
      message: "Mensaje",
      messagePlaceholder: "Contame sobre tu negocio y en qué te puedo ayudar.",
      submit: "Enviar mensaje",
      success: "Gracias. Te respondo lo antes posible, normalmente en menos de 24 horas.",
      error: "No se pudo enviar el mensaje. Probá de nuevo o escribinos por WhatsApp.",
      validation: "Revisá el nombre, un correo válido y el mensaje.",
      privacySuffix: "y autorizo el uso de estos datos para responder a mi consulta.",
    },
    final: {
      title: "¿Empezamos a construir una marca que venda con estrategia?",
      body: "Empezá por la Auditoría gratuita: escribime por WhatsApp, analizo tu perfil y tu negocio y te digo con honestidad qué tiene sentido para vos. Si querés aprender a hacerlo por tu cuenta, reservá la Asesoría Estratégica.",
    },
  },
  services: [
    {
      title: "Gestión Estratégica de Instagram",
      desc: "Convertimos tu perfil en una herramienta que atrae clientes, genera confianza y aumenta tus reservas.",
      icon: Instagram,
      iconTone: "primary",
      features: [
        "Estrategia personalizada",
        "Contenido que conecta",
        "Resultados que generan reservas",
      ],
      rung: "Delegar",
      href: "/gestion-instagram",
      ctaLabel: "Conocer más",
    },
    {
      title: "Asesoría Estratégica de Instagram",
      desc: "Aprendé a gestionar el Instagram de tu negocio con una estrategia 100% personalizada y una guía en video para avanzar a tu propio ritmo.",
      icon: Sparkles,
      iconTone: "primary",
      features: [
        "Asesoría en video personalizado",
        "Kit de Implementación Lucentia",
        "7 días de soporte por WhatsApp",
      ],
      rung: "Aprender",
      href: "/asesoria-instagram",
      ctaLabel: "Conocer más",
    },
    {
      title: "Página Web Estratégica",
      desc: "Tu página web no solo debe verse bien. Debe convertir visitas en clientes y reservas.",
      icon: Globe,
      iconTone: "primary",
      features: [
        "Diseño personalizado",
        "Sistema de reservas",
        "Optimizada para móviles",
      ],
      rung: "Convertir",
      href: "/pagina-web",
      ctaLabel: "Conocer más",
    },
  ],
  about: {
    eyebrow: "Sobre mí",
    title: "Hola, soy Jenifer, fundadora de Lucentia Social.",
    paragraphs: [
      "Creo que una buena estrategia empieza mucho antes de publicar en Instagram.",
      "Empieza por entender el negocio: qué ofrece, qué lo hace diferente, a quién quiere llegar y, sobre todo, qué necesita para crecer.",
      "Por eso creé Lucentia Social: para ayudar a negocios a dejar de hacer marketing sin rumbo y empezar a construir una presencia digital que tenga sentido para sus objetivos.",
      "Me involucro en cada proyecto para entender cómo funciona el negocio y encontrar qué puede mejorarse, simplificarse o potenciarse.",
      "Porque no creo en fórmulas que funcionan igual para todos.",
      "Cada negocio tiene una historia, un público y un objetivo diferente. Y la estrategia debería partir de ahí.",
    ],
    expectTitle: "¿Qué podés esperar al trabajar conmigo?",
    expectIntro:
      "Una mirada estratégica, comunicación clara y soluciones pensadas para tu negocio, no un paquete de acciones hechas en automático.",
    highlights: [
      {
        title: "Una estrategia pensada para tu negocio.",
        description:
          "No aplicamos fórmulas genéricas. Primero entendemos dónde estás y qué querés conseguir.",
      },
      {
        title: "Una mirada que conecta las piezas.",
        description:
          "Instagram, web, contenido y procesos tienen que trabajar juntos, no funcionar como elementos aislados.",
      },
      {
        title: "Acompañamiento cercano y comunicación clara.",
        description:
          "Vas a saber qué estamos haciendo, por qué lo hacemos y hacia dónde vamos.",
      },
      {
        title: "Decisiones con intención.",
        description:
          "Cada acción tiene un objetivo: atraer, conectar, convertir o mejorar.",
      },
    ],
    ctaLabel: "Trabajemos juntos",
  },
  methodology: {
    eyebrow: "Sistema Lucentia®",
    title: "Tu negocio necesita un sistema, no herramientas aisladas",
    subtitle:
      "Cada pieza cumple una función para atraer clientes, convertir visitas en reservas y hacer crecer tu negocio de forma organizada.",
    ctaLabel: "Conocé el Sistema Lucentia®",
    steps: [
      {
        title: "Instagram",
        desc: "Atraemos la atención de tu cliente ideal.",
        icon: Instagram,
      },
      {
        title: "Página web",
        desc: "Convertimos visitas en oportunidades.",
        icon: Monitor,
      },
      {
        title: "Sistema de reservas",
        desc: "Facilitamos el proceso para que reserven fácil y en cualquier momento.",
        icon: Calendar,
      },
      {
        title: "Automatización",
        desc: "Hacemos que tu negocio trabaje incluso cuando vos no estás.",
        icon: Settings,
      },
      {
        title: "Optimización continua",
        desc: "Medimos, analizamos y mejoramos para que tu negocio siga creciendo.",
        icon: BarChart3,
      },
    ],
    resultsTitle: "Cuando todas estas piezas trabajan juntas...",
    results: [
      "Más reservas.",
      "Menos tiempo respondiendo mensajes.",
      "Procesos organizados.",
      "Una mejor experiencia para tus clientes.",
      "Un negocio preparado para crecer.",
    ],
  },
  methodPage: {
    hero: {
      title: "¿Qué es el Sistema Lucentia®?",
      subtitle:
        "Es un sistema estratégico diseñado para ayudar a tu negocio a conseguir más reservas de forma organizada, automática y constante.",
      highlightLead: "No se trata solo de publicar en Instagram.",
      highlightBody: "Se trata de crear un sistema que haga crecer tu negocio.",
    },
    why: {
      title: "¿Por qué creamos un sistema?",
      problems: [
        "Muchos negocios publican en Instagram… pero no tienen una estrategia.",
        "Otros tienen una web… pero no genera reservas.",
        "Y muchos trabajan todo el día respondiendo WhatsApp.",
      ],
      conclusion:
        "Por eso creamos un sistema donde cada parte cumple una función.",
    },
    flow: {
      title: "Así funciona",
      items: [
        {
          title: "Instagram",
          desc: "Atraemos la atención de tu cliente ideal.",
        },
        {
          title: "Página web",
          desc: "Convertimos visitas en oportunidades.",
        },
        {
          title: "Sistema de reservas",
          desc: "Facilitamos el proceso para que reserven fácil y en cualquier momento.",
        },
        {
          title: "Automatización",
          desc: "Hacemos que tu negocio trabaje incluso cuando vos no estás.",
        },
        {
          title: "Optimización continua",
          desc: "Medimos, analizamos y mejoramos para que tu negocio siga creciendo.",
        },
      ],
      summary:
        "El Sistema Lucentia® combina estrategia, captación, conversión y optimización para que todas las piezas de tu negocio trabajen juntas.",
    },
    process: {
      title: "¿Cómo lo implementamos?",
      subtitle: "Lo hacemos con el Método Lucentia",
      stepLabel: "Paso",
      steps: [
        {
          title: "Descubrimos",
          subtitle: "Análisis Estratégico",
          description:
            "Primero conocemos tu negocio. No aplicamos soluciones genéricas. Antes de diseñar cualquier estrategia, analizamos cómo funciona tu negocio, qué está frenando su crecimiento y dónde están las oportunidades de mejora.",
          listTitle: "¿Qué analizamos?",
          items: [
            "Auditoría estratégica del negocio",
            "Análisis FODA",
            "Diagnóstico y optimización del perfil de Instagram",
            "Análisis de tu presencia digital",
            "Definición de objetivos claros",
            "Público ideal",
            "Competencia",
          ],
          footer:
            "Primero entendemos. Después diseñamos una estrategia que realmente funcione.",
        },
        {
          title: "Diseñamos",
          description:
            "Con toda la información recopilada, diseñamos una estrategia personalizada.",
          listTitle: "Definimos:",
          items: [
            "Posicionamiento",
            "Contenido",
            "Perfil",
            "Embudo",
            "Objetivos",
          ],
          footer:
            "Diseñamos con propósito. Cada decisión tiene un objetivo: hacer crecer tu negocio.",
        },
        {
          title: "Implementamos",
          description: "Es momento de poner todo en marcha.",
          items: [
            "Gestión de Instagram.",
            "Diseño Web.",
            "Sistema de reservas.",
            "Seguimiento de clientes.",
          ],
          footer: "Convertimos la estrategia en acciones reales.",
        },
        {
          title: "Automatizamos",
          description:
            "Creamos procesos que siguen funcionando incluso cuando vos no estás trabajando.",
          items: [
            "Reservas online",
            "Recordatorios automáticos",
            "Seguimiento de clientes",
            "Automatizaciones",
            "Organización de la agenda",
          ],
          footer: "Menos tareas repetitivas. Más tiempo para vos.",
        },
        {
          title: "Optimizamos",
          description: "Medimos resultados y mejoramos continuamente.",
          items: [
            "Analizamos métricas.",
            "Ajustamos la estrategia.",
            "Buscamos nuevas oportunidades.",
          ],
          footer: "Porque crecer es un proceso continuo.",
        },
      ],
    },
    results: {
      title: "¿Qué conseguís con el Método Lucentia®?",
      items: [
        "Más reservas.",
        "Un negocio organizado.",
        "Menos tiempo respondiendo mensajes.",
        "Procesos automatizados.",
        "Una estrategia clara.",
        "Un sistema pensado para crecer.",
      ],
      tagline: "Un sistema. Un método. Resultados reales.",
    },
    cta: {
      title: "¿Listo para dar el primer paso?",
      subtitle: "Todo gran crecimiento empieza con un buen diagnóstico.",
      alternativeA: "Tu negocio puede seguir dependiendo del día a día…",
      alternativeB:
        "O puede empezar a funcionar con una estrategia, un sistema y procesos que trabajen para vos.",
      description:
        "Solicitá una Auditoría Estratégica sin coste y descubrí qué necesita tu negocio para atraer más clientes, conseguir más reservas y crecer con un sistema diseñado para vos.",
      closing:
        "Descubrí si el Sistema Lucentia® es lo que tu negocio necesita.",
      buttonLabel: "Solicitar una auditoría estratégica sin coste",
      tagline: "Sistema Lucentia® — Estrategia · Sistema · Resultados",
    },
  },
  webServicePage: {
    hero: {
      eyebrow: "Diseño de",
      title: "Página Web",
      titleAccent: "Estratégica",
      subtitle:
        "Tu página web no solo debe verse bien. Debe convertir visitas en clientes y reservas.",
      highlights: [
        "Diseño personalizado",
        "Sistema de reservas",
        "Optimizada para móviles",
      ],
      closing:
        "Diseñamos páginas web que trabajan para tu negocio, mientras vos te enfocás en lo que más importa: tus clientes.",
      pillars: "Estrategia · Diseño · Sistema · Resultados",
    },
    includes: {
      title: "¿Qué incluye tu página web?",
      items: [
        {
          title: "Diseño 100 % personalizado",
          desc: "Creamos una web única que refleja la esencia de tu marca.",
        },
        {
          title: "Adaptada a móviles, tablets y ordenador",
          desc: "Experiencia perfecta en cualquier dispositivo.",
        },
        {
          title: "Sistema de reservas online",
          desc: "Tus clientes reservan 24/7 de forma rápida y sencilla.",
          note: "si tu negocio lo necesita",
        },
        {
          title: "Pasarela de pago",
          desc: "Cobra reservas, anticipos o vende productos de forma segura.",
          note: "si tu negocio lo necesita",
        },
        {
          title: "Asistente con IA",
          desc: "Responde consultas frecuentes y mejora la atención automatizada.",
        },
        {
          title: "Botón directo de WhatsApp",
          desc: "Contacto inmediato con tus clientes en un click.",
        },
        {
          title: "Integración con Google Maps",
          desc: "Tu ubicación siempre visible para que te encuentren fácilmente.",
        },
        {
          title: "Galería de imágenes o productos",
          desc: "Mostrá lo mejor de tu negocio con imágenes que enamoran.",
          note: "si corresponde a tu actividad",
        },
        {
          title: "Reseñas de Google",
          desc: "Mostrá la confianza de tus clientes directamente en tu web.",
        },
        {
          title: "Formulario de contacto",
          desc: "Facilita consultas, presupuestos y nuevos contactos.",
        },
      ],
      summary:
        "Todo lo que tu negocio necesita en un solo lugar, para brindar una experiencia profesional y convertir más.",
    },
    optimization: {
      eyebrow: "Optimizamos",
      title: "Tu web para que te encuentren, te elijan y te reserven.",
      subtitle:
        "Aplicamos las mejores estrategias de optimización para que tu negocio gane visibilidad, autoridad y convierta más.",
      items: [
        {
          title: "Optimización SEO",
          desc: "Para mejorar tu posicionamiento en Google.",
        },
        {
          title: "Optimización AEO",
          desc: "Para que tu negocio pueda aparecer como respuesta a preguntas frecuentes en buscadores y asistentes.",
        },
        {
          title: "Optimización GEO",
          desc: "Para preparar tu sitio web para los nuevos motores de búsqueda impulsados por Inteligencia Artificial.",
        },
        {
          title: "Configuración de Google Business Profile",
          desc: "Para que tu negocio destaque en Google y atraiga más clientes locales.",
        },
        {
          title: "Google Analytics",
          desc: "Medimos, analizamos y optimizamos cada acción para que tu web siga mejorando.",
        },
        {
          title: "Diseño orientado a convertir",
          desc: "Cada sección de tu web está pensada para convertir visitas en clientes y reservas.",
        },
      ],
      calloutLines: ["Más visibilidad.", "Más confianza.", "Más resultados."],
      calloutBody:
        "Estrategia, tecnología y datos trabajando para tu crecimiento.",
    },
    audience: {
      title: "Ideal para negocios que trabajan con reservas",
      items: [
        "Peluquerías",
        "Centros de estética",
        "Estudios de tatuajes y piercing",
        "Fisioterapeutas",
        "Nutricionistas",
        "Clínicas",
        "Fotógrafos",
      ],
    },
    cta: {
      philosophy:
        "Creemos en marcas con propósito, y en proyectos que crecen con estrategia.",
      description:
        "Escribime la palabra WEB y te enviaré toda la información sobre el servicio, el proceso de trabajo y cómo podemos ayudar a tu negocio a crecer.",
      buttonLabel: "Escribir WEB por WhatsApp",
      whatsappMessage: "WEB",
      pillars: [
        { title: "Estrategia que guía", desc: "Claridad para decidir." },
        { title: "Diseño que conecta", desc: "Una web que genera confianza." },
        {
          title: "Sistema que trabaja por vos",
          desc: "Reservas y procesos automatizados.",
        },
        {
          title: "Resultados que impulsan tu marca",
          desc: "Más clientes y más reservas.",
        },
      ],
    },
  },
  instagramManagementPage: {
    hero: {
      title: "Gestión Estratégica de",
      titleAccent: "Instagram",
      subtitle:
        "Convertimos tu perfil en una herramienta que atrae clientes, genera confianza y aumenta tus reservas.",
      highlights: [
        "Estrategia personalizada",
        "Contenido que conecta",
        "Comunidad que confía",
        "Resultados que generan reservas",
      ],
      closing:
        "Nosotros nos encargamos de hacer crecer tu negocio mientras vos recuperás el tiempo para lo más importante: tus clientes.",
    },
    includes: {
      title: "¿Qué incluye nuestro servicio?",
      items: [
        {
          title: "Estrategia personalizada",
          desc: "Plan adaptado a tu negocio y a tu cliente ideal.",
        },
        {
          title: "Planificación mensual",
          desc: "Calendario de contenido alineado a tus objetivos.",
        },
        {
          title: "Diseño de publicaciones",
          desc: "Piezas visuales profesionales que reflejan tu marca.",
        },
        {
          title: "Copywriting estratégico",
          desc: "Textos que conectan, convencen y generan acción.",
        },
        {
          title: "Programación del contenido",
          desc: "Publicaciones en el momento ideal para tu audiencia.",
        },
        {
          title: "Gestión de la comunidad",
          desc: "Atención de mensajes y comentarios para fortalecer la relación con tu audiencia.",
        },
        {
          title: "Análisis de métricas",
          desc: "Medimos resultados para saber qué funciona y qué podemos mejorar.",
        },
        {
          title: "Optimización continua",
          desc: "Ajustamos la estrategia cada mes para que sigas creciendo.",
        },
        {
          title: "Optimización del perfil",
          desc: "Mejoramos tu perfil para transmitir confianza y aumentar conversiones.",
        },
        {
          title: "Asesoramiento estratégico",
          desc: "Te acompaño con ideas y decisiones que impulsan tu negocio.",
        },
      ],
      summary:
        "Nosotros creamos la estrategia, vos te enfocás en brindar la mejor experiencia a tus clientes.",
      pillars: "Estrategia · Sistema · Resultados",
    },
    pitch: {
      eyebrow: "Gestión Estratégica de Instagram",
      titleBefore: "Instagram",
      titleAccent: "que trabaja",
      titleAfter: "para llenar tu agenda",
      subtitle:
        "Creamos estrategias que atraen a las personas correctas, generan confianza y las convierten en clientes fieles.",
    },
    audience: {
      title: "Ideal para negocios que trabajan con reservas",
      items: [
        "Peluquerías",
        "Centros de estética",
        "Estudios de tatuajes y piercing",
        "Fisioterapeutas",
        "Nutricionistas",
        "Clínicas",
        "Fotógrafos",
        "Peluquería canina",
        "Centros de Manicura",
      ],
      note: "Y cualquier negocio que trabaje con reservas.",
    },
    method: {
      title: "Trabajamos con el Sistema Lucentia®",
      subtitle:
        "Estrategia, sistema y resultados reales para un crecimiento sostenible.",
      steps: [
        "Descubrimos",
        "Diseñamos",
        "Implementamos",
        "Automatizamos",
        "Optimizamos",
      ],
      href: "/metodo",
      ctaLabel: "Conocé el Sistema Lucentia®",
    },
    cta: {
      description:
        "Escribime la palabra INSTAGRAM y te envío toda la información sobre el servicio, cómo trabajamos y el plan ideal para tu negocio.",
      buttonLabel: "Escribir INSTAGRAM por WhatsApp",
      whatsappMessage: "INSTAGRAM",
    },
  },
  instagramAdvisoryPage: {
    hero: {
      title: "Asesoría Estratégica de",
      titleAccent: "Instagram",
      subtitle:
        "Aprendé a gestionar el Instagram de tu negocio con una estrategia personalizada.",
      paragraphs: [
        "Recibí una asesoría 100% personalizada, adaptada exclusivamente a tu marca.",
        "Una vez que analice tu negocio, grabaré una explicación completa para vos. Recibirás un enlace privado de YouTube para verla todas las veces que necesites y avanzar a tu propio ritmo.",
      ],
      videoNote:
        "Recibirás un enlace privado de YouTube con tu asesoría personalizada para verla todas las veces que necesites y avanzar a tu propio ritmo.",
      pillars: "Estrategia que conecta. Sistemas que generan resultados.",
    },
    includes: {
      title: "¿Qué incluye?",
      groups: [
        {
          title: "Optimización completa de tu perfil",
          items: [
            "Biografía estratégica.",
            "Organización del perfil.",
            "Enlaces y configuración.",
          ],
        },
        {
          title: "Historias destacadas estratégicas",
          items: [
            "Diseño personalizado de las portadas.",
            "Organización de las historias para guiar al visitante hasta la compra o la reserva.",
          ],
        },
        {
          title: "Definición de tu cliente ideal",
          items: [
            "A quién le hablás.",
            "Cómo comunicarte con esa persona.",
            "Qué contenido necesita ver.",
          ],
        },
        {
          title: "Análisis de estadísticas",
          items: [
            "Cómo interpretar las métricas.",
            "Qué datos analizar.",
            "Cómo utilizarlas para tomar mejores decisiones.",
          ],
        },
        {
          title: "Estrategia de contenido",
          items: [
            "Pilares de contenido.",
            "Ideas de publicaciones.",
            "Frecuencia recomendada.",
          ],
        },
        {
          title: "Historias y hashtags",
          items: [
            "Cómo crear historias que generen conexión.",
            "Estrategia de hashtags para aumentar tu alcance.",
          ],
        },
      ],
    },
    kit: {
      title: "Al terminar tu asesoría recibís además este kit para ponerla en práctica.",
      kitTitle: "Kit de Implementación Lucentia",
      items: [
        {
          title: "Guía de Optimización del Perfil",
          desc: "Una guía práctica para que tengas siempre a mano cómo optimizar tu perfil, qué información incluir y cómo organizarlo estratégicamente para transmitir confianza y convertir visitantes en clientes.",
        },
        {
          title: "7 ideas de contenido personalizadas para tu marca",
          desc: "Recibirás un plan de contenido creado exclusivamente para tu negocio, basado en tus servicios, tus objetivos y el tipo de cliente que querés atraer.",
          details: [
            "Formato (Reel o Carrusel).",
            "Pilar de contenido.",
            "Objetivo de la publicación.",
            "Copywriting listo para adaptar.",
            "Llamado a la acción (CTA).",
          ],
        },
      ],
      summary:
        "Todo pensado para que puedas crear, editar y publicar tu contenido con una estrategia clara y adaptada a tu marca.",
      noteTitle: "Importante",
      note: "Esta asesoría incluye la estrategia y las ideas personalizadas para tu negocio. No incluye el diseño de las publicaciones. El objetivo es que tengas una guía clara para crear, editar y publicar el contenido con la identidad visual de tu marca y puedas seguir utilizándola cada vez que la necesites.",
    },
    support: {
      title: "7 días de soporte de dudas vía WhatsApp",
      description:
        "Durante 7 días posteriores a recibir tu asesoría podrás escribirme por WhatsApp para resolver todas tus dudas sobre la implementación de la estrategia y acompañarte mientras empiezas a aplicarla.",
      highlight:
        "Acompañamiento real para que no estés sola en el proceso. Tu crecimiento es mi prioridad.",
    },
    cta: {
      title: "Reservá tu Asesoría Estratégica",
      description:
        "Respondé unas preguntas sobre tu negocio para que pueda analizar tu cuenta y después elegí día y hora en el calendario. Si preferís empezar con un diagnóstico sin coste, pedí la Auditoría gratuita por WhatsApp.",
      buttonLabel: "Reservar asesoría estratégica",
      whatsappMessage: "Hola Jenifer, quiero la auditoría gratuita.",
    },
  },
  audit: {
    eyebrow: "Auditoría estratégica sin coste",
    title: "Descubrí qué oportunidades tiene tu negocio para conseguir más reservas",
    description:
      "Solicitá tu Auditoría Estratégica sin coste y recibí un diagnóstico personalizado con oportunidades concretas para crecer.",
    ctaLabel: "Pedir auditoría por WhatsApp",
    whatsappLabel: "Auditoría gratuita",
  },
  resourcesPage: {
    title: "Recursos y guías",
    description: "Recursos gratuitos para impulsar tu negocio.",
  },
};
